import type { ProgramWeekRow, WorkoutRow } from '@st/contract';
import { useTranslation } from '../../../i18n/I18nProvider';
import { DAY_INDEXES, DAY_KEYS } from '../../../lib/program';
import { Badge, Button } from '../../../ui';
import { WORKOUT_TYPE_KEY } from './categories';

export interface WeekColumnProps {
  week: ProgramWeekRow;
  index: number;
  busy: boolean;
  onAddWorkout: (dayIndex: number) => void;
  onOpenWorkout: (workout: WorkoutRow) => void;
  onDuplicateWorkout: (workout: WorkoutRow) => void;
  onDeleteWorkout: (workout: WorkoutRow) => void;
  onDuplicateWeek: () => void;
  onDeleteWeek: () => void;
}

/**
 * Jedan tjedan sa svih 7 dana.
 *
 * Na širokim ekranima tjedni stoje jedan uz drugi kao kolone; ispod `lg` roditelj
 * ih slaže okomito pa se program čita tjedan po tjedan, bez bočnog pomicanja.
 */
export function WeekColumn({
  week,
  index,
  busy,
  onAddWorkout,
  onOpenWorkout,
  onDuplicateWorkout,
  onDeleteWorkout,
  onDuplicateWeek,
  onDeleteWeek,
}: WeekColumnProps) {
  const { t } = useTranslation();
  const title = week.labelHr ?? t('builder.week', { n: index + 1 });

  return (
    <section
      aria-label={title}
      className="flex flex-col gap-3 rounded-xl border border-line-subtle bg-bg-alt p-3 lg:w-[19rem] lg:shrink-0"
    >
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="eyebrow">{t('builder.week', { n: index + 1 })}</p>
          <h2 className="truncate font-display text-[18px] font-semibold text-ink">{title}</h2>
        </div>
        <div className="flex shrink-0 gap-1">
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            aria-label={`${t('builder.duplicateWeek')} — ${title}`}
            onClick={onDuplicateWeek}
          >
            {t('action.duplicate')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            aria-label={`${t('builder.deleteWeek')} — ${title}`}
            onClick={onDeleteWeek}
          >
            {t('action.delete')}
          </Button>
        </div>
      </header>

      <ol className="flex flex-col gap-2">
        {DAY_INDEXES.map((day) => {
          const workout = week.workouts.find((w) => w.dayIndex === day);
          const dayLabel = t(DAY_KEYS[day] ?? '');
          return (
            <li key={day} className="rounded-lg bg-surface p-3 shadow-card">
              <p className="mb-2 text-[11px] uppercase tracking-[2px] text-ink-muted">{dayLabel}</p>

              {workout ? (
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => onOpenWorkout(workout)}
                    className="text-left"
                    aria-label={`${t('builder.editWorkout')} — ${workout.nameHr}`}
                  >
                    <span className="block text-[14.5px] leading-tight text-ink">
                      {workout.nameHr}
                    </span>
                    <span className="mt-1 block text-[12px] text-ink-muted">
                      {t('builder.exerciseCount', { n: workout.exercises.length })}
                      {workout.estimatedMinutes
                        ? ` · ${t('dash.minutes', { n: workout.estimatedMinutes })}`
                        : ''}
                    </span>
                  </button>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone="neutral">
                      {t(WORKOUT_TYPE_KEY[workout.type] ?? workout.type)}
                    </Badge>
                    <button
                      type="button"
                      disabled={busy}
                      aria-label={`${t('builder.duplicateWorkout')} — ${workout.nameHr}`}
                      onClick={() => onDuplicateWorkout(workout)}
                      className="text-[12px] text-ink-muted underline-offset-2 hover:text-ink hover:underline disabled:opacity-40"
                    >
                      {t('action.duplicate')}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      aria-label={`${t('builder.deleteWorkout')} — ${workout.nameHr}`}
                      onClick={() => onDeleteWorkout(workout)}
                      className="text-[12px] text-ink-muted underline-offset-2 hover:text-danger hover:underline disabled:opacity-40"
                    >
                      {t('action.delete')}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onAddWorkout(day)}
                  aria-label={`${t('builder.addWorkout')} — ${dayLabel}`}
                  className="w-full rounded-md border border-dashed border-line py-3 text-[13px] text-ink-muted transition-colors duration-200 hover:border-accent-dark hover:text-accent-dark disabled:opacity-40"
                >
                  + {t('builder.addWorkout')}
                </button>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
